import React, { createContext, useCallback, useContext, useState } from 'react';
import { setEntityId, getEntityId } from '../api/client';
import { MULTI_ENTITY_ENABLED, pickDefaultEntity } from '../config/features';
import type { EntityPerm } from './AuthContext';

type EntityContextValue = {
  currentEntityId: number | null;
  currentEntityName: string | null;
  allowedEntities: EntityPerm[];
  setCurrentEntity: (id: number | null) => void;
  setAllowedEntities: (entities: EntityPerm[]) => void;
};

const EntityContext = createContext<EntityContextValue | null>(null);

export function EntityProvider({ children }: { children: React.ReactNode }) {
  const [currentEntityId, setCurrentEntityIdState] = useState<number | null>(() => getEntityId());
  const [currentEntityName, setCurrentEntityName] = useState<string | null>(null);
  const [allowedEntities, setAllowedEntities] = useState<EntityPerm[]>([]);

  const setAllowedEntitiesAndMaybeSelect = useCallback((entities: EntityPerm[]) => {
    setAllowedEntities(entities);
    if (entities.length > 0) {
      let chosen: EntityPerm | undefined;
      if (MULTI_ENTITY_ENABLED) {
        const savedId = getEntityId();
        const preferred = savedId != null ? entities.find((e) => e.entity_id === savedId) : null;
        chosen = preferred ?? entities[0];
      } else {
        chosen = pickDefaultEntity(entities);
      }
      if (!chosen) return;
      setEntityId(chosen.entity_id);
      setCurrentEntityIdState(chosen.entity_id);
      setCurrentEntityName(chosen.entity_name || null);
    } else {
      setEntityId(null);
      setCurrentEntityIdState(null);
      setCurrentEntityName(null);
    }
  }, []);

  const setCurrentEntity = useCallback((id: number | null) => {
    if (!MULTI_ENTITY_ENABLED) {
      const locked = pickDefaultEntity(allowedEntities);
      if (!locked) return;
      setEntityId(locked.entity_id);
      setCurrentEntityIdState(locked.entity_id);
      setCurrentEntityName(locked.entity_name || null);
      return;
    }
    setEntityId(id);
    setCurrentEntityIdState(id);
    const ent = allowedEntities.find((e) => e.entity_id === id);
    setCurrentEntityName(id != null && ent ? ent.entity_name : null);
  }, [allowedEntities]);

  const value: EntityContextValue = {
    currentEntityId,
    currentEntityName,
    allowedEntities,
    setCurrentEntity,
    setAllowedEntities: setAllowedEntitiesAndMaybeSelect,
  };

  return <EntityContext.Provider value={value}>{children}</EntityContext.Provider>;
}

export function useEntity() {
  const ctx = useContext(EntityContext);
  if (!ctx) throw new Error('useEntity must be used within EntityProvider');
  return ctx;
}
